import { useEffect, useRef, useCallback } from "react";
/**
 * Tracks whether the page is currently backgrounded (another app/tab in the
 * foreground) or has *recently* returned from the background.
 *
 * Mobile browsers freeze a backgrounded PWA, so a call transport that died
 * while the user was away is often only *detected* right after they return.
 * The grace window attributes those late disconnect events to the app switch,
 * so the UI can say "you left the app" instead of a generic connection error.
 */
const RETURN_GRACE_MS = 10_000;
export function useBackgroundedTracker() {
    const hiddenRef = useRef(typeof document !== "undefined" && document.visibilityState === "hidden");
    const lastReturnRef = useRef(0);
    useEffect(() => {
        const onVisibility = () => {
            if (document.visibilityState === "hidden") {
                hiddenRef.current = true;
            }
            else {
                if (hiddenRef.current)
                    lastReturnRef.current = Date.now();
                hiddenRef.current = false;
            }
        };
        const onPageHide = () => {
            hiddenRef.current = true;
        };
        document.addEventListener("visibilitychange", onVisibility);
        window.addEventListener("pagehide", onPageHide);
        return () => {
            document.removeEventListener("visibilitychange", onVisibility);
            window.removeEventListener("pagehide", onPageHide);
        };
    }, []);
    return useCallback(() => hiddenRef.current ||
        (lastReturnRef.current > 0 && Date.now() - lastReturnRef.current < RETURN_GRACE_MS), []);
}
